import { useTranslation } from 'react-i18next';
import { isSameDay, isAfter, parseISO, startOfDay } from 'date-fns';
import { Icon, type IconName } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CalendarView } from '@/components/calendar';
import { NeedsAttentionCard } from './NeedsAttentionCard';
import { UpcomingVisitsCard } from './UpcomingVisitsCard';
import { resolveGreetingPeriod } from './greetingPeriod';
import { useAuth } from '@/hooks/useAuth';
import { useClientsQuery } from '@/hooks/useClientsQuery';
import { useWorkspacePatientsQuery } from '@/hooks/usePatientsQueries';
import { useAppointmentsQuery } from '@/hooks/useAppointmentsQuery';
import type { Appointment } from '@/api/appointments/appointmentsApi.types';
import { AssistantTargetRegion } from '@/components/assistant/uiActions/AssistantTargetRegion';
import { ASSISTANT_TARGET_KEY } from '@/components/assistant/uiActions/assistantTargetKeys';

function countUpcomingAppointments(appointments: Appointment[]): number {
  const todayStart = startOfDay(new Date());
  return appointments.filter(function keepTodayAndFuture(appointment) {
    const appointmentDate = parseISO(appointment.date);
    return isSameDay(appointmentDate, todayStart) || isAfter(appointmentDate, todayStart);
  }).length;
}

function countAppointmentsToday(appointments: Appointment[]): number {
  const today = new Date();
  return appointments.filter(function keepToday(appointment) {
    return isSameDay(parseISO(appointment.date), today);
  }).length;
}

export function PractitionerDashboard() {
  const { t } = useTranslation(['dashboard', 'common']);
  const { currentUser } = useAuth();

  const firstNameCandidate =
    currentUser === null ? '' : currentUser.fullName.trim().split(/\s+/)[0];
  const firstName = firstNameCandidate === undefined ? '' : firstNameCandidate;

  const clientsQuery = useClientsQuery(1, '', '');
  const patientsQuery = useWorkspacePatientsQuery(1, '');
  const appointmentsQuery = useAppointmentsQuery();
  const appointments = appointmentsQuery.data === undefined ? [] : appointmentsQuery.data;

  const stats: StatCardDefinition[] = [
    {
      id: 'clients',
      iconName: 'Users',
      labelKey: 'dashboard:practitioner.stats.clients',
      value: clientsQuery.data === undefined ? null : clientsQuery.data.totalItems,
      isLoading: clientsQuery.isLoading,
      isError: clientsQuery.isError,
      onRetry: function retryClients() {
        void clientsQuery.refetch();
      },
    },
    {
      id: 'patients',
      iconName: 'PawPrint',
      labelKey: 'dashboard:practitioner.stats.patients',
      value: patientsQuery.data === undefined ? null : patientsQuery.data.totalItems,
      isLoading: patientsQuery.isLoading,
      isError: patientsQuery.isError,
      onRetry: function retryPatients() {
        void patientsQuery.refetch();
      },
    },
    {
      id: 'upcoming',
      iconName: 'Clock',
      labelKey: 'dashboard:practitioner.stats.upcoming',
      value: appointmentsQuery.data === undefined ? null : countUpcomingAppointments(appointments),
      isLoading: appointmentsQuery.isLoading,
      isError: appointmentsQuery.isError,
      onRetry: function retryUpcoming() {
        void appointmentsQuery.refetch();
      },
    },
    {
      id: 'today',
      iconName: 'Calendar',
      labelKey: 'dashboard:practitioner.stats.today',
      value: appointmentsQuery.data === undefined ? null : countAppointmentsToday(appointments),
      isLoading: appointmentsQuery.isLoading,
      isError: appointmentsQuery.isError,
      onRetry: function retryToday() {
        void appointmentsQuery.refetch();
      },
    },
  ];

  const greetingPeriod = resolveGreetingPeriod(new Date().getHours());
  const greetingText =
    firstName.length === 0
      ? t('dashboard:practitioner.greetingGeneric' + greetingPeriod)
      : t('dashboard:practitioner.greeting' + greetingPeriod, { name: firstName });

  return (
    <div className="p-6 md:p-8 flex flex-col gap-6 [container-type:inline-size]">
      <div>
        <h1 className="text-2xl font-bold text-zooindigo flex items-center gap-2">
          {greetingText}
          <Icon name="PawPrint" className="text-zooviolet" />
        </h1>
        <p className="text-zoomuted text-sm mt-1">{t('dashboard:practitioner.overviewSubtitle')}</p>
      </div>

      <div className="grid grid-cols-2 gap-4 [@container(min-width:52rem)]:grid-cols-4">
        {stats.map(function renderStatCard(stat) {
          return <StatCard key={stat.id} definition={stat} />;
        })}
      </div>

      <div className="flex flex-wrap items-start gap-6">
        <AssistantTargetRegion
          targetKey={ASSISTANT_TARGET_KEY.dashboardCalendar}
          className="min-h-[560px] min-w-0 flex-[999_1_40rem]"
        >
          <CalendarView />
        </AssistantTargetRegion>
        <div className="min-w-0 flex-[1_1_18rem] order-first [@container(min-width:59.5rem)]:order-none flex flex-col gap-6">
          <NeedsAttentionCard />
          <UpcomingVisitsCard />
        </div>
      </div>
    </div>
  );
}

interface StatCardDefinition {
  id: string;
  iconName: IconName;
  labelKey: string;
  value: number | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

function StatCard({ definition }: { definition: StatCardDefinition }) {
  const { t } = useTranslation(['dashboard', 'common']);

  if (definition.isError === true) {
    return (
      <Card className="flex items-center gap-4">
        <span className="w-11 h-11 rounded-xl bg-zooerrorbg text-zooerrortext flex items-center justify-center shrink-0">
          <Icon name="AlertCircle" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-zooerrortext">
            {t('dashboard:practitioner.stats.loadError')}
          </div>
          <div className="text-xs text-zoomuted">{t(definition.labelKey)}</div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t('common:actions.retry')}
          onClick={definition.onRetry}
        >
          <Icon name="RefreshCw" />
        </Button>
      </Card>
    );
  }

  return (
    <Card className="flex items-center gap-4">
      <span className="w-11 h-11 rounded-xl bg-zoovpalelavender text-zooprimary flex items-center justify-center shrink-0">
        <Icon name={definition.iconName} />
      </span>
      <div className="min-w-0">
        <div className="text-2xl font-bold text-zooindigo leading-none">
          {definition.isLoading === true || definition.value === null ? '—' : definition.value}
        </div>
        <div className="text-xs text-zoomuted mt-1">{t(definition.labelKey)}</div>
      </div>
    </Card>
  );
}
